import React, {useEffect, useRef} from "react"

import {isEmpty} from "lodash"
import PropTypes from "prop-types"
import {Modal as SemanticUIModal} from "semantic-ui-react"

import {FOCUSABLE_ELEMENTS_SELECTORS} from "../../utils/selectorUtil"
import ButtonPrimary from "../ButtonPrimary/buttonPrimary"
import ButtonSecondary from "../ButtonSecondary/buttonSecondary"
import IconButton from "../IconButton/iconButton"

import "./modal.scss"

const Modal = ({
    actionsContent = null,
    closeButtonDisabled = false,
    closeButtonIsLoading = false,
    closeFunction,
    closeLabel = null,
    confirmButtonDisabled = false,
    confirmButtonIsLoading = false,
    confirmFunction,
    confirmLabel = null,
    confirmSecondaryButtonDisabled = false,
    confirmSecondaryButtonIsLoading = false,
    confirmSecondaryFunction = () => {},
    confirmSecondaryLabel = null,
    content,
    contentScrolling = true,
    contentSize = "medium",
    displayCloseIcon = true,
    isOpen,
    rightCloseLabel = null,
    title = "",
    ...props
}) => {
    const headerRef = useRef(null)
    const contentRef = useRef(null)
    const actionsRef = useRef(null)

    // Mise en place d'un FocusTrap -> permet de ne pas donner le focus aux elements extérieurs à la modale
    useEffect(() => {
        if (isOpen) {
            // On récupère les élements focusables
            const headerElement = headerRef.current
            const contentElement = contentRef.current
            const actionsElement = actionsRef.current

            const headerFocusableElements = headerElement.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTORS)
            const contentFocusableElements = contentElement.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTORS)
            const actionsFocusableElements = actionsElement.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTORS)
            const focusableElements = [
                ...headerFocusableElements,
                ...contentFocusableElements,
                ...actionsFocusableElements,
            ]

            if (focusableElements.length > 0) {
                const firstElement = focusableElements[0]
                const lastElement = focusableElements[focusableElements.length - 1]

                const handleKeyPress = event => {
                    switch (event.key) {
                        case "Tab":
                            if (event.shiftKey && document.activeElement === firstElement) {
                                event.preventDefault()
                                lastElement.focus()
                            } else if (!event.shiftKey && document.activeElement === lastElement) {
                                event.preventDefault()
                                firstElement.focus()
                            }
                            break
                        case "Escape":
                            closeFunction()
                            break
                        default:
                            break
                    }
                }

                headerElement.addEventListener("keydown", handleKeyPress)
                contentElement.addEventListener("keydown", handleKeyPress)
                actionsElement.addEventListener("keydown", handleKeyPress)

                return () => {
                    headerElement.removeEventListener("keydown", handleKeyPress)
                    contentElement.removeEventListener("keydown", handleKeyPress)
                    actionsElement.removeEventListener("keydown", handleKeyPress)
                }
            }
        }
    }, [closeFunction, isOpen])

    return (
        <SemanticUIModal
            aria-describedby="modal-content"
            aria-labelledby="modal-title"
            aria-modal="true"
            className={`modal ${contentSize}-content`}
            onClose={closeFunction}
            open={isOpen}
            {...props}
        >
            <SemanticUIModal.Header className="modal-header">
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                <div className="stealth-focus" tabIndex="0"></div>
                <header ref={headerRef}>
                    <h2 id="modal-title">{title}</h2>
                    {displayCloseIcon && (
                        <IconButton
                            ariaLabel="Fermer la fenêtre modale"
                            dataTestId="boutonFermerModale"
                            icon="fa-solid fa-xmark"
                            onClick={closeFunction}
                        />
                    )}
                </header>
            </SemanticUIModal.Header>
            <SemanticUIModal.Content
                aria-label="Contenu de la modale"
                className="modal-content"
                id="modal-content"
                scrolling={contentScrolling}
            >
                <div ref={contentRef}>{content}</div>
            </SemanticUIModal.Content>
            <SemanticUIModal.Actions>
                <div aria-label="Actions de la modale" className="modal-actions" ref={actionsRef} role="toolbar">
                    {/* Contenu personnalisé du footer */}
                    {!!actionsContent && actionsContent}

                    {/* Contenu par défaut */}
                    {isEmpty(actionsContent) && (
                        <>
                            {closeLabel && (
                                <ButtonSecondary
                                    disabled={closeButtonDisabled}
                                    loading={closeButtonIsLoading}
                                    onClick={closeFunction}
                                    size={contentSize}
                                >
                                    {closeLabel}
                                </ButtonSecondary>
                            )}
                            {confirmLabel && (
                                <ButtonPrimary
                                    disabled={confirmButtonDisabled}
                                    loading={confirmButtonIsLoading}
                                    onClick={confirmFunction}
                                    size={contentSize}
                                >
                                    {confirmLabel}
                                </ButtonPrimary>
                            )}
                            {confirmSecondaryLabel && (
                                <ButtonPrimary
                                    disabled={confirmSecondaryButtonDisabled}
                                    loading={confirmSecondaryButtonIsLoading}
                                    onClick={confirmSecondaryFunction}
                                    size={contentSize}
                                >
                                    {confirmSecondaryLabel}
                                </ButtonPrimary>
                            )}
                            {rightCloseLabel && (
                                <ButtonSecondary
                                    disabled={closeButtonDisabled}
                                    loading={closeButtonIsLoading}
                                    onClick={closeFunction}
                                    size={contentSize}
                                >
                                    {rightCloseLabel}
                                </ButtonSecondary>
                            )}
                        </>
                    )}
                </div>
            </SemanticUIModal.Actions>
        </SemanticUIModal>
    )
}

Modal.propTypes = {
    actionsContent: PropTypes.oneOfType([PropTypes.object, PropTypes.elementType]),
    closeButtonDisabled: PropTypes.bool,
    closeButtonIsLoading: PropTypes.bool,
    closeFunction: PropTypes.func.isRequired,
    closeLabel: PropTypes.string,
    confirmButtonDisabled: PropTypes.bool,
    confirmButtonIsLoading: PropTypes.bool,
    confirmFunction: PropTypes.func,
    confirmLabel: PropTypes.string,
    confirmSecondaryButtonDisabled: PropTypes.bool,
    confirmSecondaryButtonIsLoading: PropTypes.bool,
    confirmSecondaryFunction: PropTypes.func,
    confirmSecondaryLabel: PropTypes.string,
    content: PropTypes.oneOfType([PropTypes.object, PropTypes.elementType]).isRequired,
    contentScrolling: PropTypes.bool,
    contentSize: PropTypes.oneOf(["small", "medium"]),
    displayCloseIcon: PropTypes.bool,
    isOpen: PropTypes.bool.isRequired,
    rightCloseLabel: PropTypes.string,
    title: PropTypes.string,
}

export default Modal
